export interface ApiContributor {
  id: string;
  displayName: string;
  email: string | null;
}

export interface ApiWish {
  id: string;
  title: string;
  note: string;
  edition: string;
  imageUrl: string;
  merchant: string;
  sourceUrl: string;
  priceCents: number;
  pledgedCents: number;
  contributorCount: number;
  mode: 'pledge' | 'gift' | 'choose';
  status: string;
  deadline: string;
  messageRevealed: boolean;
  giftMessages: string[];
  giftMessageCount: number;
  myContributionId: string | null;
  myReservationId: string | null;
  amCaptain: boolean;
  hasCaptain?: boolean;
  isReserved: boolean;
}

export interface ApiWishlist {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  people: string;
  deliveryInstructions: string;
  currency: string;
  occasion: string;
  paymentUrl: string;
  paymentLabel: string;
  wishes: ApiWish[];
}

export interface ApiOwnerAccount {
  email: string;
  plan: 'free' | 'pass' | 'plus';
  canCollect: boolean;
  listLimit: number | null;
  wishLimit: number | null;
  subscriptionStatus: string;
  subscriptionPeriodEnd: string;
  passExpiresAt: string;
}

export interface ApiOwnerWishlist {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  people: string;
  deliveryInstructions: string;
  currency: string;
  occasion: string;
  isPublished: boolean;
  wishCount: number;
  paymentUrl: string;
  paymentLabel: string;
  plan: 'free' | 'pass' | 'plus';
  canCollect: boolean;
  listLimit: number | null;
  wishLimit: number | null;
  passExpiresAt: string;
}

interface ApiErrorBody {
  error?: {code?: string; message?: string};
}

export class SouhaitApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, status: number, code = 'request_failed') {
    super(message);
    this.name = 'SouhaitApiError';
    this.status = status;
    this.code = code;
  }
}

const request = async <T>(
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...options,
    headers: {
      ...(options.body ? {'content-type': 'application/json'} : {}),
      ...options.headers,
    },
  });
  const value = (await response.json()) as T & ApiErrorBody;
  if (!response.ok) {
    throw new SouhaitApiError(
      (value.error && value.error.message) || 'Une erreur est survenue.',
      response.status,
      (value.error && value.error.code) || 'request_failed'
    );
  }
  return value;
};

export const getSession = () =>
  request<{contributor: ApiContributor | null}>('/api/session');

export const createSession = (
  displayName: string,
  email?: string,
  turnstileToken?: string
) =>
  request<{
    contributor: ApiContributor;
    recoveryCode: string;
  }>('/api/session', {
    method: 'POST',
    body: JSON.stringify({displayName, email, turnstileToken}),
  });

export const loadWishlist = (slug: string, sort = 'position') =>
  request<{wishlist: ApiWishlist; contributor: ApiContributor | null}>(
    `/api/wishlists/${encodeURIComponent(slug)}?sort=${encodeURIComponent(sort)}`
  );

export const pledgeToWish = (
  wishId: string,
  amountCents: number,
  privateMessage: string,
  recipientMessage: string,
  coordinationOptIn: boolean,
  coordinationEmail: string
) =>
  request<{
    contribution: {id: string; amountCents: number; status: string};
    fullyFunded: boolean;
  }>(`/api/wishes/${encodeURIComponent(wishId)}/contributions`, {
    method: 'POST',
    body: JSON.stringify({
      amountCents,
      privateMessage,
      recipientMessage,
      coordinationOptIn,
      coordinationEmail,
      acceptTerms: true,
    }),
  });

export const reserveWish = (wishId: string, privateMessage: string) =>
  request<{reservation: {id: string; status: string}}>(
    `/api/wishes/${encodeURIComponent(wishId)}/reservations`,
    {
      method: 'POST',
      body: JSON.stringify({privateMessage, acceptTerms: true}),
    }
  );

export const becomeCaptain = (wishId: string) =>
  request<{ok: boolean; status: string}>(
    `/api/wishes/${encodeURIComponent(wishId)}/captain`,
    {method: 'POST', body: '{}'}
  );

export const sendCaptainUpdate = (wishId: string, message: string) =>
  request<{ok: boolean; recipientCount: number}>(
    `/api/wishes/${encodeURIComponent(wishId)}/captain/updates`,
    {method: 'POST', body: JSON.stringify({message})}
  );

export const revealGiftMessages = (wishId: string) =>
  request<{ok: boolean; revealed: boolean}>(
    `/api/wishes/${encodeURIComponent(wishId)}/captain/reveal`,
    {method: 'POST', body: '{}'}
  );

export interface ActivityItem {
  id: string;
  status: string;
  createdAt: string;
  wishId: string;
  title: string;
  slug: string;
  amountCents?: number;
  privateMessage?: string;
  paymentUrl?: string;
  paymentLabel?: string;
}

export const loadActivity = () =>
  request<{
    contributor: ApiContributor;
    contributions: ActivityItem[];
    reservations: ActivityItem[];
    captainships: ActivityItem[];
  }>('/api/me/activity');

export const updateContribution = (
  wishId: string,
  contributionId: string,
  action: 'settle' | 'cancel'
) =>
  request<{ok: boolean; status: string}>(
    `/api/wishes/${encodeURIComponent(
      wishId
    )}/contributions/${encodeURIComponent(contributionId)}/${action}`,
    {method: 'POST', body: '{}'}
  );

export const updateReservation = (
  wishId: string,
  reservationId: string,
  action: 'confirm' | 'cancel'
) =>
  request<{ok: boolean; status: string}>(
    `/api/wishes/${encodeURIComponent(
      wishId
    )}/reservations/${encodeURIComponent(reservationId)}/${action}`,
    {method: 'POST', body: '{}'}
  );

export const logout = () =>
  request<{ok: boolean}>('/api/session', {method: 'DELETE'});

export const recoverSession = (
  recoveryCode: string,
  turnstileToken: string
) =>
  request<{contributor: ApiContributor}>('/api/session/recover', {
    method: 'POST',
    body: JSON.stringify({recoveryCode, turnstileToken}),
  });

export const previewOwnerMetadata = (url: string) =>
  request<{
    metadata: {
      url: string;
      title: string;
      merchant: string;
      imageUrl: string;
      price: string;
      currency: string;
      provider: string;
      productId: string;
      status: 'complete' | 'partial';
    };
  }>('/api/admin/metadata-preview', {
    method: 'POST',
    body: JSON.stringify({url}),
  });

export const listOwnerWishlists = () =>
  request<{account: ApiOwnerAccount; wishlists: ApiOwnerWishlist[]}>(
    '/api/admin/wishlists'
  );

export const getOwnerSession = () =>
  request<{account: ApiOwnerAccount | null}>('/api/owner/session');

export const requestOwnerLogin = (
  email: string,
  turnstileToken = ''
) =>
  request<{ok: boolean; developmentCode?: string}>('/api/owner/session', {
    method: 'POST',
    body: JSON.stringify({email, turnstileToken}),
  });

export const verifyOwnerLogin = (email: string, code: string) =>
  request<{account: ApiOwnerAccount}>('/api/owner/session/verify', {
    method: 'POST',
    body: JSON.stringify({email, code}),
  });

export const logoutOwner = () =>
  request<{ok: boolean}>('/api/owner/session', {method: 'DELETE'});

export const createOwnerWishlist = (wishlist: {
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  people?: string;
  deliveryInstructions?: string;
  occasion?: string;
}) =>
  request<{wishlist: ApiOwnerWishlist}>('/api/admin/wishlists', {
    method: 'POST',
    body: JSON.stringify(wishlist),
  });

export const updateOwnerWishlistPayment = (
  slug: string,
  paymentUrl: string,
  paymentLabel: string
) =>
  request<{ok: boolean; paymentUrl: string; paymentLabel: string}>(
    `/api/admin/wishlists/${encodeURIComponent(slug)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({paymentUrl, paymentLabel}),
    }
  );

export const startBillingCheckout = (
  kind: 'pass' | 'monthly' | 'annual',
  wishlistId = ''
) =>
  request<{url: string}>('/api/owner/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({kind, wishlistId}),
  });

export const openBillingPortal = () =>
  request<{url: string}>('/api/owner/billing/portal', {
    method: 'POST',
    body: '{}',
  });

export const trackEvent = (
  eventName: string,
  routeKind: string,
  properties: {[key: string]: string | number | boolean} = {}
) =>
  request<{ok: boolean}>('/api/events', {
    method: 'POST',
    body: JSON.stringify({
      eventName, routeKind, properties,
      campaign: new URLSearchParams(window.location.search).get('campaign') || 'direct',
    }),
  }).catch(() => undefined);

export const paymentProviderCategory = (url: string) => {
  try {
    const host = new URL(url).hostname;
    if (host === 'paypal.me' || host === 'paypal.com' || host.endsWith('.paypal.com')) return 'paypal';
    if (host === 'stripe.com' || host.endsWith('.stripe.com')) return 'stripe';
  } catch { /* Invalid URLs are rejected before navigation. */ }
  return 'other';
};

export const createOwnerWish = (
  slug: string,
  wish: {
    title: string;
    note: string;
    edition?: string;
    imageUrl: string;
    merchant: string;
    sourceUrl: string;
    priceCents: number;
    mode: 'pledge' | 'gift' | 'choose';
    deadline?: string;
  }
) =>
  request<{wish: {id: string}}>(
    `/api/admin/wishlists/${encodeURIComponent(slug)}/wishes`,
    {method: 'POST', body: JSON.stringify(wish)}
  );

export const updateOwnerWish = (
  wishId: string,
  wish: {
    title: string;
    note: string;
    edition?: string;
    imageUrl: string;
    merchant: string;
    sourceUrl: string;
    priceCents: number;
    mode: 'pledge' | 'gift' | 'choose';
    deadline?: string;
  }
) =>
  request<{ok: boolean}>(`/api/admin/wishes/${encodeURIComponent(wishId)}`, {
    method: 'PATCH',
    body: JSON.stringify(wish),
  });

export const archiveOwnerWish = (wishId: string) =>
  request<{ok: boolean}>(`/api/admin/wishes/${encodeURIComponent(wishId)}`, {
    method: 'DELETE',
    body: '{}',
  });

export const connectWishlistEvents = (
  slug: string,
  onChange: () => void
) => {
  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let refreshTimer: number | null = null;
  let stopped = false;

  const connect = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    socket = new WebSocket(
      `${protocol}//${window.location.host}/api/wishlists/${encodeURIComponent(
        slug
      )}/events`
    );
    socket.addEventListener('message', event => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'wishlist.changed') {
          if (refreshTimer !== null) window.clearTimeout(refreshTimer);
          refreshTimer = window.setTimeout(() => {
            refreshTimer = null;
            onChange();
          }, 120);
        }
      } catch {
        // Ignore keep-alive frames.
      }
    });
    socket.addEventListener('close', () => {
      if (!stopped) reconnectTimer = window.setTimeout(connect, 1500);
    });
  };

  connect();
  return () => {
    stopped = true;
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    if (refreshTimer !== null) window.clearTimeout(refreshTimer);
    if (socket) socket.close(1000, 'Page left');
  };
};
